import { useRef, useState } from 'react';
import { Box } from '@mui/system';
import { Button, Input } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { cardSx } from './styles';
import { type Mission } from 'src/utils/type';
import InstructionDialog from 'src/components/Dialog/Image';
import { S3_URL } from '../../../utils/constant';
import CircleLineProgress from 'src/components/Progress/CircleLine';
import ExpiredMissionDialog from 'src/components/Dialog/ExpiredMission';
import { ReactComponent as UploadIcon } from 'src/assets/icon_upload.svg';
import { addSubmission, uploadFile } from 'src/api/apis';
import { useHistory } from 'react-router-dom';

export default function MissionCard ({
  mission,
  allowEdit = false,
  report = false,
  onClickEdit = () => {},
  simple = false,
}: {
  mission: Mission;
  allowEdit?: boolean;
  report?: boolean;
  onClickEdit?: () => void;
  simple?: boolean;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const [isOpened, setIsOpened] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [showInstruction, setShowInstruction] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCompleted, setIsCompleted] = useState(mission.submitted > 0);
  const endTime = new Date(mission.end);
  const currentTime = new Date();

  const handleOpen = () => {
    if (report) {
      history.push(`/mission-report/${mission.id}`);
      return;
    }
    if (endTime >= currentTime) {
      setIsOpened(prev => !prev);
    } else {
      setReminderOpen(true);
    }
  };

  const handleOpenInstruction = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setShowInstruction(true);
  };

  const handleUpload = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleSubmit = (file: File) => {
    uploadFile(file).then(response => {
      const imageUrl = response.data.file_url;
      if (imageUrl) {
        addSubmission({
          mission_id: mission.id,
          image_url: imageUrl,
        }).then(response => {
          if (response.data.status === '1') {
            setIsCompleted(true);
          } else {
            alert('Submit failed');
          }
        });
      } else {
        alert('Upload failed');
      }
    });
  };

  const handleEdit = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onClickEdit();
  };

  const action = () => {
    if (report) return <></>;

    if (allowEdit) {
      return (
        <Button
          variant="contained"
          className={'action ' + (endTime >= currentTime ? 'live' : 'expired')}
          onClick={handleEdit}
        >
          {t('Edit')}
        </Button>
      );
    }

    if (isCompleted) return <div className="action completed">{t('Completed')}</div>;

    if (endTime >= currentTime) {
      return (
        <div className="action live" onClick={handleUpload}>
          <UploadIcon />
          <span>{t('Upload')}</span>
          <Input
            type="file"
            inputRef={fileInputRef}
            sx={{ display: 'none' }}
            onChange={(e: any) => {
              const file = e.target.files?.[0];
              if (file) handleSubmit(file);
            }}
          />
        </div>
      );
    }
    return <div className="action expired">{t('Expired')}</div>;
  };

  return simple
    ? (
    <Box
      sx={{
        padding: '10px',
        boxShadow: '0px 3px 6px #00000029',
        '& .score': {
          marginTop: '15px',
          fontSize: '14px',
          background: '#006EC2',
          padding: '2px 4px',
          display: 'inline-block',
          lineHeight: '17px',
          borderRadius: '2px',
          color: '#FFFFFF',
        },
      }}
    >
      <div style={{ marginTop: '15px', color: '#006EC2', fontSize: '24px', fontWeight: 'bold' }}>
        {mission.title.en}
      </div>
      <span className="score">{mission.score} score</span>
      <div style={{ fontSize: '18px', color: '#006EC2', marginTop: '15px' }}>
        {t('End Time')}: {convertTimeString(mission.end)}
      </div>

      <p style={{ fontSize: '18px' }}>{mission.description}</p>
    </Box>
      )
    : (
    <>
      <Box component="figure" sx={cardSx} onClick={handleOpen}>
        <div className="wrapper">
          <div className="header">
            <div className="header-left">
              <span className="title">{mission.title.en}</span>
              <span className="score">
                {mission.score} {t('score')}
              </span>
            </div>
            <div className="header-right">{action()}</div>
          </div>
          <div className="info">
            <span>
              {t('End Time')}: {convertTimeString(mission.end)}
            </span>
          </div>
          <div className="content">{mission.description}</div>
          {isOpened && (
            <div className="detail">
              <div className="detail-thumbnail" onClick={handleOpenInstruction}>
                <img src={S3_URL(mission.steps[currentStep].image_url)} alt="thumbnail" width={'100%'} />
              </div>
              <div className="detail-steps">
                <span className="title">{t('Mission step')}</span>
                <div className="step-progress">
                  <CircleLineProgress
                    stepNumber={mission.steps.length}
                    currentStep={currentStep}
                    setStep={(number: number) => { setCurrentStep(number); }}
                  />
                </div>
                <div className="step-description">{mission.steps[currentStep].description.en}</div>
              </div>
            </div>
          )}
          <div className="footer">{action()}</div>
        </div>
      </Box>
      {showInstruction && (
        <InstructionDialog
          url={S3_URL(mission.steps[currentStep].image_url)}
          onClose={() => {
            setShowInstruction(false);
          }}
        />
      )}
      <ExpiredMissionDialog
        show={reminderOpen}
        onClose={() => {
          setReminderOpen(false);
        }}
      />
    </>
      );
}

function convertTimeString (time: string) {
  const date = new Date(time);
  const padding = (num: number) => (num < 10 ? `0${num}` : num);

  return `${padding(date.getDate())}/${padding(date.getMonth() + 1)}/${padding(date.getFullYear())} ${padding(
    date.getHours(),
  )}:${padding(date.getMinutes())}`;
}
